import { useCallback, useEffect, useMemo, useState } from 'react';
import { Option } from '../forms/option';
import { DEFAULT_RESULTS_PER_PAGE } from './constants';

export type PaginationContextType = {
    itemsPerPage: number;
    setItemsPerPage: (itemsPerPage: number) => void;
    currentPage: number;
    setCurrentPage: (currentPage: number) => void;
    canGoToPreviousPage: boolean;
    goToPreviousPage: () => void;
    canGoToNextPage: boolean;
    goToNextPage: () => void;
    totalItemCount: number;
    totalItemCountDisplay: string;
    setTotalItemCount: (totalItemCount: number) => void;
    totalPageCount: number;
    totalPageCountDisplay: string;
    itemOffset: number;
    pageOptions: Option<number>[];
};

export function usePagination(): PaginationContextType {
    const [totalItemCount, setTotalItemCount] = useState<number>(0);
    const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_RESULTS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState<number>(1);
    useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage]);
    const totalPageCount = useMemo(() => Math.ceil(totalItemCount / itemsPerPage), [
        totalItemCount,
        itemsPerPage,
    ]);
    const canGoToPreviousPage = useMemo(() => currentPage > 1, [currentPage]);
    const goToPreviousPage = useCallback(() => {
        if (canGoToPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    }, [canGoToPreviousPage, currentPage]);
    const canGoToNextPage = useMemo(() => currentPage < totalPageCount, [
        currentPage,
        totalPageCount,
    ]);
    const goToNextPage = useCallback(() => {
        if (canGoToNextPage) {
            setCurrentPage(currentPage + 1);
        }
    }, [canGoToNextPage, currentPage]);
    const itemOffset = currentPage * itemsPerPage - itemsPerPage;
    const pageOptions = useMemo(() => {
        return [...Array(totalPageCount).keys()].map((page) => ({
            label: `${page + 1}`,
            value: page + 1,
        }));
    }, [totalPageCount]);
    return {
        itemsPerPage,
        setItemsPerPage,
        currentPage,
        setCurrentPage,
        canGoToPreviousPage,
        goToPreviousPage,
        canGoToNextPage,
        goToNextPage,
        totalItemCount,
        totalItemCountDisplay: totalItemCount.toLocaleString(),
        setTotalItemCount,
        totalPageCount,
        totalPageCountDisplay: totalPageCount.toLocaleString(),
        itemOffset,
        pageOptions,
    };
}
